import type { MediaTarget } from '@/api/core/property-media';
import { LoadFailure } from '@/components/load-failure';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import type { PropertyRuntime } from '../property-queries';
import { MediaItemsPanel } from './media-items-panel';
import { categoryLabel, languageLabel, purposeLabel } from './media-presentation';
import { propertyMediaGroupsQuery } from './media-queries';
import { MediaRetirementRecovery } from './media-retirement-form';
import { MediaWriteOwnerProvider } from './media-write-form';
import { NativeTypeMediaSection } from './native-type-media-section';
import { OwnMediaCreate } from './own-media-create';

export function NativeAssetMediaSection(props: Parameters<typeof AssetMediaSection>[0]) {
  return (
    <MediaWriteOwnerProvider
      key={props.propertyId}
      runtime={props.runtime}
      propertyId={props.propertyId}
    >
      <AssetMediaSection key={JSON.stringify(props.target)} {...props} />
    </MediaWriteOwnerProvider>
  );
}

function AssetMediaSection({
  readEnabled = true,
  runtime,
  generation,
  propertyId,
  target,
  label,
  eager = false,
  onEditRoomType,
}: {
  readEnabled?: boolean;
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  target: MediaTarget;
  label: string;
  eager?: boolean;
  onEditRoomType?: (typeId: string) => void;
}) {
  const [open, setOpen] = useState(eager);
  const heading = useRef<HTMLHeadingElement>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  // 사진 추가를 눌러 연 묶음만 업로드 창을 자동으로 연다. 목록에서 묶음을 고르기만 할 때는 열지 않는다.
  const [addNonce, setAddNonce] = useState(0);
  const [preparedGroupId, setPreparedGroupId] = useState<string | null>(null);
  const cache = useQueryClient();
  const isCurrent = useCallback(() => {
    const s = runtime.session.getSnapshot();
    return (
      s.status === 'authorized' &&
      s.generation === generation &&
      !s.deniedDomains.includes('property')
    );
  }, [runtime, generation]);
  const options = propertyMediaGroupsQuery(
    runtime,
    generation,
    propertyId,
    target,
    readEnabled && open && isCurrent(),
    isCurrent,
    true,
  );
  const query = useInfiniteQuery(options);
  const pages = query.data?.pages;
  const scope = pages?.at(-1)?.scope;
  const roomTypeId = scope?.typeRelationState === 'RESOLVED' ? scope.roomTypeId : null;
  const groups = [...new Map(pages?.flatMap((p) => p.groups).map((g) => [g.id, g])).values()];
  const activeGroupId = groupId ?? groups[0]?.id ?? null;
  const bound = pages?.every(
    (p) =>
      p.scope.propertyId.toLowerCase() === propertyId.toLowerCase() &&
      p.scope.floor === target.floor &&
      p.scope.targetKind === target.kind &&
      p.scope.targetId?.toLowerCase() === target.id?.toLowerCase(),
  );
  const visibleCollectionId =
    bound &&
    activeGroupId &&
    (preparedGroupId === activeGroupId || groups.some((g) => g.id === activeGroupId))
      ? activeGroupId
      : null;
  const ownPhotos = (
    <section aria-label={`${label} 사진`} className="space-y-3 rounded-md border p-4">
      <h3 ref={heading} tabIndex={-1} className="font-semibold">
        사진
      </h3>
      <p className="body-sm text-muted-foreground mt-2">
        {target.kind === 'ROOM'
          ? '이 객실에 직접 등록한 사진'
          : target.kind === 'SLOT'
            ? '이 슬롯에 직접 등록한 사진'
            : '직접 등록 사진'}
      </p>
      {!open && (
        <Button
          className="mt-2"
          variant="outline"
          onClick={() => {
            setOpen(true);
          }}
        >
          {label} 사진 보기
        </Button>
      )}
      {open && (
        <>
          {query.isFetching && !query.isPending && (
            <p role="status">사진 그룹을 새로 불러오고 있습니다.</p>
          )}
          {query.isPending && <p role="status">사진 그룹을 불러오고 있습니다.</p>}
          {query.isError && (
            <LoadFailure
              error={query.error}
              subject="사진 그룹"
              onRetry={() => void cache.resetQueries({ queryKey: options.queryKey, exact: true })}
            />
          )}
          {pages && !bound && <p role="alert">사진 대상이 일치하지 않습니다.</p>}
          {bound && !groups.length && !query.isError && (
            <p>이 자산에 등록된 사진 그룹이 없습니다.</p>
          )}
          {bound &&
            !query.isError &&
            (target.kind === 'PROPERTY' ||
              (target.kind === 'SLOT' && target.id && pages?.[0]?.scope.roomId)) && (
              <OwnMediaCreate
                runtime={runtime}
                generation={generation}
                existingCollectionId={
                  groups.find(
                    (group) =>
                      group.id === (groupId ?? (groups.length === 1 ? groups[0]?.id : null)) &&
                      group.presentationId === null &&
                      group.collectionRevision !== null &&
                      group.collectionRevision > 0,
                  )?.id
                }
                owner={{
                  owner_scope_kind: target.kind === 'PROPERTY' ? 'PROPERTY' : 'UNIT',
                  owner_property_id: propertyId,
                  owner_room_type_id: null,
                  owner_room_id:
                    target.kind === 'PROPERTY' ? null : (pages?.[0]?.scope.roomId ?? null),
                  owner_unit_id: target.kind === 'PROPERTY' ? null : target.id,
                  purpose: 'GALLERY',
                  language: 'ko',
                  name: null,
                  sort_order: 0,
                  category_key: null,
                }}
                onSaved={async (id) => {
                  await cache.resetQueries({ queryKey: options.queryKey, exact: true });
                  setPreparedGroupId(id);
                  setGroupId(id);
                  setAddNonce((n) => n + 1);
                }}
              />
            )}
          {bound && groups.length > 1 && (
            <details className="my-3">
              <summary className="cursor-pointer">다른 사진 보기 · {groups.length}개 분류</summary>
              <ul className="mt-2 flex max-h-48 flex-wrap gap-2 overflow-auto">
                {groups.map((g) => (
                  <li key={g.id}>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setGroupId(g.id);
                        setAddNonce(0);
                      }}
                    >
                      {purposeLabel(g.purpose)} · {languageLabel(g.language)} ·{' '}
                      {categoryLabel(g.categoryName)}
                    </Button>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {query.hasNextPage && (
            <Button disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
              다음 사진 그룹
            </Button>
          )}
        </>
      )}
      <MediaRetirementRecovery
        runtime={runtime}
        generation={generation}
        propertyId={propertyId}
        target={target}
        isCurrent={isCurrent}
        visibleCollectionId={visibleCollectionId}
        onSaved={async (command) => {
          await cache.resetQueries({ queryKey: options.queryKey, exact: true });
          if (!command.itemId) {
            setGroupId((id) => (id === command.collectionId ? null : id));
            setPreparedGroupId((id) => (id === command.collectionId ? null : id));
          }
          heading.current?.focus();
        }}
      />
      {bound &&
        activeGroupId &&
        (preparedGroupId === activeGroupId || groups.some((g) => g.id === activeGroupId)) && (
          <MediaItemsPanel
            readEnabled={readEnabled}
            key={`${activeGroupId}:${String(addNonce)}`}
            runtime={runtime}
            generation={generation}
            propertyId={propertyId}
            groupId={activeGroupId}
            expectedTarget={target}
            inline
            adding={addNonce > 0}
            nativeCurrent
            allowNativeWrite={target.kind === 'PROPERTY' || target.kind === 'SLOT'}
            isCurrent={isCurrent}
            scopeLabel={() => label}
            onClose={() => {
              heading.current?.focus();
              setPreparedGroupId(null);
              setGroupId(null);
            }}
          />
        )}
    </section>
  );
  return (
    <>
      {ownPhotos}
      {bound &&
        !query.isError &&
        onEditRoomType &&
        (target.kind === 'ROOM' || target.kind === 'SLOT') &&
        roomTypeId && (
          <section aria-label="객실 유형 공통 사진" className="space-y-3">
            <NativeTypeMediaSection
              readEnabled={readEnabled}
              key={roomTypeId}
              runtime={runtime}
              generation={generation}
              propertyId={propertyId}
              typeId={roomTypeId}
              typeName="객실 유형 공통 사진"
              readOnly
            />
            <Button
              variant="outline"
              onClick={() => {
                onEditRoomType(roomTypeId);
              }}
            >
              객실 유형 사진 편집
            </Button>
          </section>
        )}
    </>
  );
}
